import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { AuthenticationContext } from '../../providers/AuthenticationContext';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import Toggle from '../../common/components/Toggle/Toggle';
import Log from '../../common/logging';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';
import Loading from '../../common/components/Loading/Loading';
import RenewableToken from '../../providers/token/RenewableToken';
import { Routes } from '../../config/routes';
import ErrorWithCode from '../../common/errors/errorWithCode';

function AuthenticationScreen(): JSX.Element {
  const { t } = useTranslation();
  const { performLogin, token, retrievingExistingAuth } = useContext(AuthenticationContext);
  const navigate = useNavigate();
  const { networkName, chainName, navigateToNextFlow, hideWalletPrompts, flowType } = useContext(FlowConfigContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [loginCompleted, setLoginCompleted] = useState<boolean>(false);
  const [usingLedger, setUsingLedger] = useState<boolean>(false);
  const currentLocation = useLocation();

  const login = async () => {
    setLoading(true);
    Log.debug('usingLedger', usingLedger);
    const loginResult = await performLogin(usingLedger);

    if (loginResult.isFailed()) {
      // handle the error and navigate to the error screen, allowing the error screen to navigate back here if the user wants to
      loginResult.logError('AuthenticationScreen: Error performing login');
      const error = loginResult.failure().nestedError as ErrorWithCode;
      Log.debug('Error performing login', loginResult.failure());
      setLoading(false);
      navigate(Routes.CHAIN_ERROR, { state: { errorCode: error.code, prevPath: currentLocation.pathname } });
    } else {
      setLoginCompleted(true);
    }
  };

  // when hideWalletPrompts is true, we should automatically login
  useEffect(() => {
    if (hideWalletPrompts) {
      login();
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    if (loginCompleted || (!retrievingExistingAuth && token !== null && token instanceof RenewableToken)) {
      navigateToNextFlow();
    }
    // eslint-disable-next-line consistent-return
    return () => {
      abortController.abort();
    };
  }, [loginCompleted, token, retrievingExistingAuth, navigateToNextFlow]);

  const handleConfirm = () => {
    login();
  };

  const labelPath = 'screens.authentication';

  return hideWalletPrompts || retrievingExistingAuth ? (
    <Container>
      <Loading />
    </Container>
  ) : (
    <Container>
      <TitleContainer center>
        <h1 data-testid="AUTHENTICATION_SCREEN" className="mb-4">
          {t(`${labelPath}.title`, { context: networkName, flowType: t(`${flowType}`) })}
        </h1>
        <div className="leading-6 mb-4 text-textParagraph pass-status-text text-lg">
          {usingLedger ? (
            <ReactMarkdown linkTarget="_blank" className="whitespace-pre-line">
              {t(`${labelPath}.description.zeroCostTransaction`, { context: networkName, flowType: t(`${flowType}`) })}
            </ReactMarkdown>
          ) : (
            <ReactMarkdown linkTarget="_blank" className="whitespace-pre-line">
              {t(`${labelPath}.description.signMessage`, { context: networkName, flowType: t(`${flowType}`) })}
            </ReactMarkdown>
          )}
        </div>
      </TitleContainer>
      {chainName === 'solana' ? (
        <div className="mb-4 flex items-center">
          <Toggle
            enabled={usingLedger}
            alwaysShowLabels
            setEnabled={setUsingLedger}
            labels={{
              on: ``,
              off: t('screens.authentication.usingLedger'),
            }}
          />
        </div>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      )}
      <ButtonContainer bottomMargin="mb-4">
        <Button
          style={ButtonStyle.DEFAULT}
          disabled={false}
          loading={loading}
          dataTestid="OK_BUTTON"
          onClick={handleConfirm}
        >
          <span>{t('screens.authentication.continueButton', { context: networkName })}</span>
        </Button>
      </ButtonContainer>
    </Container>
  );
}
export default withRouteAnalytics(AuthenticationScreen, Screens.AUTHENTICATION);
